import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
    ArrowLeft, Loader2, Save, CheckCircle2, AlertCircle, Tag, MapPin
} from "lucide-react";
import { ImageUpload } from "../../components/ui/image-upload";
import { projectsService, type Project } from "../../../lib/services/projectsService";
import type { Models } from "appwrite";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";

type ProjectDoc = Project & Models.Document;

const CATEGORIES = [
    "HVAC",
    "Heat Pump",
    "Cold Room",
    "Electrical",
    "Refrigerant ISO Tank",
    "Industrial HVAC"
];

const EMPTY: Omit<Project, "$id"> = {
    title: "",
    category: CATEGORIES[0],
    location: "",
    scope: "",
    outcome: "",
    status: "Ongoing",
    slug: "",
    imageUrl: "",
};

export function AdminProjectEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = !id || id === "new";

    const [form, setForm] = useState<Omit<Project, "$id">>(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        projectsService.list()
            .then((projects) => {
                const p = (projects as ProjectDoc[]).find((x) => x.$id === id);
                if (p) {
                    setForm({
                        title: p.title,
                        category: p.category,
                        location: p.location,
                        scope: p.scope,
                        outcome: p.outcome,
                        status: p.status,
                        slug: p.slug,
                        imageUrl: p.imageUrl ?? "",
                    });
                }
            })
            .catch(() => showToast("error", "Failed to load project."))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const set = (k: keyof typeof form, v: string) =>
        setForm((f) => {
            const upd = { ...f, [k]: v };
            // Auto-generate slug from title if missing/new
            if (k === "title" && (isNew || !f.slug)) {
                upd.slug = v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
            }
            return upd;
        });

    const showToast = (type: "success" | "error", msg: string) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3500);
    };

    const handleSave = async () => {
        if (!form.title.trim()) { showToast("error", "Project title is required."); return; }
        if (!form.location.trim()) { showToast("error", "Location is required."); return; }
        if (!form.outcome.trim()) { showToast("error", "Outcome is required."); return; }

        setSaving(true);
        try {
            if (isNew) {
                await projectsService.create(form);
                showToast("success", "Project added successfully.");
            } else {
                await projectsService.update(id!, form);
                showToast("success", "Changes saved.");
            }
            setTimeout(() => navigate("/admin/projects"), 1200);
        } catch (e: any) {
            showToast("error", e?.message ?? "Failed to save project.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="w-full min-w-0">
            {/* ── Sticky top bar ── */}
            <div className="sticky top-0 z-10 bg-background border-b border-border">
                <div className="flex items-center gap-3 px-4 sm:px-6 h-14">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/projects")}
                        className="gap-1.5 text-muted-foreground hover:text-white -ml-1 shadow-none">
                        <ArrowLeft className="w-4 h-4" /> Projects
                    </Button>
                    <div className="h-4 w-px bg-border" />
                    <span className="text-sm font-medium text-foreground truncate flex-1">
                        {form.title || (isNew ? "New Project" : "Edit Project")}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 shadow-none">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            {isNew ? "Add Project" : "Save"}
                        </Button>
                    </div>
                </div>
                {toast && (
                    <div className={`flex items-center gap-2 px-6 py-2 text-sm border-t ${toast.type === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-destructive/5 text-destructive border-destructive/10"}`}>
                        {toast.type === "success"
                            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                            : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                        {toast.msg}
                    </div>
                )}
            </div>

            {/* ── Two-column body: Content LEFT, Settings RIGHT ── */}
            <div className="flex flex-col lg:flex-row min-h-[calc(100vh-3.5rem)]">

                {/* ── LEFT: Main product details ── */}
                <div className="flex-1 p-4 sm:p-6 space-y-5 border-r-0 lg:border-r border-border">

                    <div className="space-y-1.5">
                        <Label htmlFor="p-title" className="text-xs font-medium text-muted-foreground">Project Title *</Label>
                        <Input
                            id="p-title"
                            placeholder="e.g. Central HVAC System — Shopping Mall"
                            value={form.title}
                            onChange={(e) => set("title", e.target.value)}
                            className="shadow-none text-base font-medium h-10"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="p-slug" className="text-xs font-medium text-muted-foreground">URL Slug</Label>
                        <div className="flex items-center">
                            <span className="px-3 border border-r-0 border-border rounded-l-md bg-muted text-sm text-muted-foreground flex items-center h-9">
                                /projects/
                            </span>
                            <Input
                                id="p-slug"
                                value={form.slug}
                                onChange={(e) => set("slug", e.target.value)}
                                className="shadow-none h-9 rounded-l-none"
                                placeholder="central-hvac-system"
                            />
                        </div>
                    </div>

                    <div className="border-t border-border" />

                    <div className="space-y-1.5">
                        <Label htmlFor="p-outcome" className="text-xs font-medium text-muted-foreground">Outcome / Description *</Label>
                        <Textarea
                            id="p-outcome"
                            rows={6}
                            placeholder="e.g. 42-TR VRF system serving 3 floors, 18°C maintained year-round..."
                            value={form.outcome}
                            onChange={(e) => set("outcome", e.target.value)}
                            className="resize-y shadow-none min-h-[120px]"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="p-scope" className="text-xs font-medium text-muted-foreground">Project Scope *</Label>
                        <Input
                            id="p-scope"
                            placeholder="e.g. Survey · Design · Installation · AMC"
                            value={form.scope}
                            onChange={(e) => set("scope", e.target.value)}
                            className="shadow-none h-9"
                        />
                    </div>
                </div>

                {/* ── RIGHT: Settings sidebar ── */}
                <div className="w-full lg:w-72 xl:w-80 shrink-0 p-4 sm:p-6 space-y-5 border-t lg:border-t-0 border-border bg-muted/20">

                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Project Settings</p>

                    {/* Status */}
                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                        <Select value={form.status} onValueChange={(v) => set("status", v)}>
                            <SelectTrigger className="shadow-none h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Ongoing">Ongoing</SelectItem>
                                <SelectItem value="Completed">Completed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Category + Location */}
                    <div className="space-y-3 border-t border-border pt-4">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Category *</Label>
                            <Select value={form.category} onValueChange={(v) => set("category", v)}>
                                <SelectTrigger className="shadow-none h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="p-loc" className="text-xs font-medium text-muted-foreground">Location *</Label>
                            <Input id="p-loc" placeholder="e.g. Jaipur, Rajasthan" value={form.location}
                                onChange={(e) => set("location", e.target.value)} className="shadow-none h-9" />
                        </div>
                    </div>

                    {/* Cover Image */}
                    <div className="space-y-2 border-t border-border pt-4">
                        <ImageUpload
                            label="Project Image"
                            value={form.imageUrl}
                            onChange={(url) => set("imageUrl", url)}
                            aspectRatio="video"
                        />
                    </div>

                    {/* Preview badges */}
                    <div className="pt-2 border-t border-border flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${form.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {form.status}
                        </Badge>
                        <Badge variant="outline" className="text-xs">{form.category}</Badge>
                        {form.location && (
                            <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-transparent px-2 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {form.location}
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
